import type { ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";

export type ColumnAlign = "start" | "center" | "end";

export interface TableColumn<Row> {
  readonly id: string;
  readonly header: ReactNode;
  readonly cell: (row: Row) => ReactNode;
  /**
   * Logical alignment: `start` = right in Arabic / left in English. Defaults to `end`
   * for numeric columns, `start` otherwise.
   */
  readonly align?: ColumnAlign;
  /** Numbers: end-aligned, tabular digits, always LTR digit order. */
  readonly numeric?: boolean;
}

const ALIGN: Readonly<Record<ColumnAlign, string>> = {
  start: "text-start",
  center: "text-center",
  end: "text-end",
};

export interface TableProps<Row> {
  readonly columns: ReadonlyArray<TableColumn<Row>>;
  readonly rows: ReadonlyArray<Row>;
  readonly getRowId: (row: Row) => string;
  /** Accessible table name. */
  readonly caption: string;
  readonly hideCaption?: boolean;
  /** Shown when `rows` is empty (defaults to the catalog's `ui.table.empty`). */
  readonly emptyMessage?: string;
  readonly className?: string;
}

/**
 * Data table. Column order follows the writing direction (first column at the inline
 * start) and every alignment is logical, so it mirrors in Arabic with no extra code.
 */
export function Table<Row>({
  columns,
  rows,
  getRowId,
  caption,
  hideCaption = true,
  emptyMessage,
  className,
}: TableProps<Row>) {
  const { t } = useI18n();
  const alignOf = (c: TableColumn<Row>) => ALIGN[c.align ?? (c.numeric ? "end" : "start")];
  return (
    <div className={cx("overflow-x-auto rounded-2xl border border-line bg-surface", className)}>
      <table className="w-full border-collapse text-sm">
        <caption className={cx("px-4 py-3 text-start font-semibold", hideCaption && "sr-only")}>
          {caption}
        </caption>
        <thead className="bg-surface-muted">
          <tr>
            {columns.map((c) => (
              <th
                key={c.id}
                scope="col"
                className={cx(
                  "px-4 py-3 font-semibold whitespace-nowrap text-fg-muted",
                  alignOf(c),
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-fg-muted">
                {emptyMessage ?? t("ui.table.empty")}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={getRowId(row)} className="border-t border-line hover:bg-surface-muted">
                {columns.map((c) => (
                  <td
                    key={c.id}
                    className={cx("px-4 py-3 text-fg", alignOf(c), c.numeric && "tabular-nums")}
                  >
                    {c.numeric ? <bdi dir="ltr">{c.cell(row)}</bdi> : c.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
